import { useEffect } from 'react';
import Navigation from './routes/Navigation';
import { GrowthBookProvider } from '@growthbook/growthbook-react';
import { GoogleOAuthProvider } from '@react-oauth/google';
import growthbookFile from './shared/constants/growthbook';
import { useSelector } from 'react-redux';
import { type RootState } from './redux/store';
import { QueryClientProvider } from '@tanstack/react-query';
import { CurpExtractionProvider } from './features/onboarding/context/CurpExtractionContext';
import {
	identifyRecordedUser,
	initSessionRecording,
} from './shared/analytics/sessionRecording';
import { queryClient } from './shared/queryClient';
import { ensureFreshSession } from './shared/auth/tokenRefresh';
import { MaintenanceGate } from './features/maintenance/components/MaintenanceGate';
import { AppUpdateGate } from './features/app-update/components/AppUpdateGate';

// Todo lo que arranca con la app (sesión, grabaciones, router) vive aquí abajo
// para que, en modo mantenimiento, `MaintenanceGate` no lo monte: sin llamadas a
// nuestras APIs mientras el backend o la base están en trabajo.
function AppRuntime() {
	const userProfile = useSelector((state: RootState) => state.profile.user);

	// Identidad de grabaciones (Amplitude + Clarity) en UN solo punto: todo
	// camino de login termina en setProfile → este effect. Idempotente.
	useEffect(() => {
		if (!userProfile?.id) return;
		identifyRecordedUser(userProfile.id);
	}, [userProfile?.id]);

	useEffect(() => {
		initSessionRecording();
	}, []);

	// Refresco proactivo de sesión: al arrancar y cada vez que la pestaña / PWA
	// vuelve a primer plano, si al access token le queda poco se renueva con el
	// refresh token antes de que un 401 interrumpa al usuario. Single-flight y
	// con umbral, así que es inocuo repetirlo. Si falla, no cierra sesión.
	useEffect(() => {
		void ensureFreshSession();
		const onVisible = () => {
			if (document.visibilityState === 'visible') void ensureFreshSession();
		};
		document.addEventListener('visibilitychange', onVisible);
		return () => document.removeEventListener('visibilitychange', onVisible);
	}, []);

	return (
		<CurpExtractionProvider>
			<Navigation />
		</CurpExtractionProvider>
	);
}

function App() {
	return (
		<QueryClientProvider client={queryClient}>
			<GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_OAUTH_CLIENT_ID}>
				<GrowthBookProvider growthbook={growthbookFile}>
					<MaintenanceGate>
						<AppUpdateGate>
							<AppRuntime />
						</AppUpdateGate>
					</MaintenanceGate>
				</GrowthBookProvider>
			</GoogleOAuthProvider>
		</QueryClientProvider>
	);
}

export default App;
